import { $document } from '@umu/umu-dom';
import { getCurrentSystem } from '@umu/ukit-lang';
import {isDingding, isDingdingDesktop, isFeishu} from '@umu/umu-ua';

export * from './@datetime';
export * from './number';
export * from './string';
export * from './log';
export * from './object';
export * from '@umu/ukit-lang';
export * from './regexp';
export * from './array';
export * from './file';
export * from './animation';
export * from './deferred';
export * from './eventEmitterWrapper';
export * from './keyboard';
export * from './crypto';
export * from './rgbaster';
export * from './interface';
export * from './umu';
export * from "./dom-to-image"
export * from './designConfig/datePicker';
export * from './designConfig/select';
export * from './currency';
export * from "./violation";

export * from './rtl';


/**
 * @desc
 * @param
 */
export interface ICancelableFunction {
    (...args: any[]): any;
    cancel: () => void;
    [props: string]: any;
}

/**
 * 兼容不是 JSON 格式的字符串，不是 JSON 返回 false
 * @param str String
 * @returns Object | false
 */
export const jsonParse = (str: string) => {
    let data: object | false = {};

    try {
        data = JSON.parse(str);
    } catch (error) {
        data = false;
    }
    return data;
};

/**
 * debounce
 * via: screen
 */
export const debounce = (
    func: (...args: any[]) => any,
    wait: number,
    context?: any,
    immediate = false,
): ((...args: any[]) => any) => {
    context = context || null;
    let timeout: number | null;
    return (...args: any[]) => {
        const later = () => {
            timeout = null;
            if (!immediate) {
                func.call(context, ...args);
            }
        };
        const callNow = immediate && !timeout;
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = window.setTimeout(later, wait);
        if (callNow) {
            func.call(context, ...args);
        }
    };
};

/**
 * throttle
 */
export const throttle = (fn: (...args: any[]) => any, wait: number, context?: any) => {
    let nextFn: number | any;
    let lastTimer: number;
    const newFunction = ((...args: any[]) => {
        if (!lastTimer) {
            fn.apply(context, args);
            lastTimer = Date.now();
        } else {
            clearTimeout(nextFn);
            nextFn = setTimeout(() => {
                if (Date.now() - lastTimer >= wait) {
                    fn.apply(context, args);
                    lastTimer = Date.now();
                }
            }, wait - (Date.now() - lastTimer));
        }
    }) as ICancelableFunction;

    newFunction.cancel = () => {
        clearTimeout(nextFn);
    };
    return newFunction;
};

/**
 * 与 throttle 的区别是，保证最后一次一定会执行
 */
export function throttleGuarantee<T extends any[]>(func: (...args: T) => void, interval: number): (...args: T) => void {
    let timer: NodeJS.Timeout | null = null;
    let previous = 0;
    let lastArgs: T | null = null;

    return function (...args: T) {
        const now = Date.now();
        const remaining = interval - (now - previous);

        lastArgs = args;

        if (remaining <= 0) {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }

            func(...args);
            previous = now;
        } else if (!timer) {
            timer = setTimeout(() => {
                if (lastArgs) {
                    func(...lastArgs);
                    lastArgs = null;
                }
                timer = null;
                previous = Date.now();
            }, remaining);
        }
    };
}

/**
 * @desc
 * @param
 */
export function interval(fn: (...args: any[]) => any, wait: number, context?: any) {
    let timer: number | null;
    const newFunction = ((...args: any[]) => {
        newFunction.cancel();
        timer = window.setInterval(() => {
            fn.apply(context, args);
        }, wait);
    }) as ICancelableFunction;
    newFunction.cancel = () => {
        if (timer) {
            clearInterval(timer);
        }
        timer = null;
    };
    return newFunction;
}

/**
 * @desc
 * @param
 */
export function once(fn: (...args: any[]) => any, context?: any) {
    const newFunc = (...args: any[]) => {
        const result = fn.apply(context, args);
        fn = () => void 0;
        return result;
    };
    return newFunc;
}

let idCounter = 0;
/**
 * @desc
 * @param
 */
export function getUniqueId(prefix: string = '') {
    const id = idCounter++;
    return (prefix ? prefix + id : id) + '';
}

/**
 * @desc
 * @param
 */
export function parseUrl(url: string) {
    const l = document.createElement('a');
    l.href = url;
    return l;
}

/**
 * @desc
 * @param
 */
export function cancelableTimeout(callback: (...args: any[]) => void, wait: number, context?: any) {
    let timer: number;
    const newFunction = ((...args: any[]) => {
        newFunction.cancel();
        timer = window.setTimeout(() => {
            callback.apply(context, args);
        }, wait);
    }) as ICancelableFunction;
    newFunction.cancel = () => {
        clearTimeout(timer);
    };
    return newFunction;
}

/**
 * @desc
 * @param
 */
export function getGuid() {
    return Date.now() + Math.floor(Math.random() * 1000) + '';
}

/**
 * 通过点击a标签的方式打开窗口
 * @param {*} url 完整的url
 * @param {*} target 以何种方式打开 '_blank' | '_self'
 * @param {*} openType a-link | window, default a-link
 * @author Kody
 */
export const openWindow = (url: string, target: '_blank' | '_self' = '_blank', openType: 'a-link' | 'window' = 'a-link', args?: any): any => {
    // @see https://app.asana.com/0/1203622320206279/1203645972204374
    // 桌面版钉钉强制打开当前页面
    let realTarget = target;
    if (isDingding || isDingdingDesktop || isFeishu) {
        realTarget = '_self';
    }

    if (openType === 'window') {
        return window.open(url, realTarget, args);
    } else {
        // todo 下面这句不该放在这里，用什么url不是openwindow的职责
        url = /^http/.test(url) ? url : `https://${url}`;
        const alink = $document.createElement('a');
        alink.href = url;
        alink.target = realTarget;
        alink.rel = 'noopener';
        alink.click();
        if (alink.remove) {
            alink.remove();
        }
    }
};

type anyFnc = (text: any) => any;
export function compose(_arg1: anyFnc, _arg2?: anyFnc, _arg3?: anyFnc): anyFnc {
    const _len = arguments.length;
    const funcs = Array(_len);
    for (let _key = 0; _key < _len; _key++) {
        funcs[_key] = arguments[_key];
    }

    // tslint:disable-next-line:only-arrow-functions
    return function () {
        if (funcs.length === 0) {
            return arguments.length <= 0 ? undefined : arguments[0];
        }

        const last = funcs[funcs.length - 1];
        const rest = funcs.slice(0, -1);

        return rest.reduceRight((composed, f) => {
            return f(composed);
        }, last.apply(undefined, arguments));
    };
}

declare global {
    interface Document {
        fullscreenElement: Element;
        webkitFullscreenElement?: Element;
        msFullscreenElement?: Element;
        mozFullscreenElement?: Element;
    }
}

export const checkIsFullscreen = () => {
    const el = document;
    return !!(el.fullscreenElement || el.webkitFullscreenElement || el.msFullscreenElement || el.mozFullscreenElement);
};

/**
 * 下载兼容https
 */
export const httpToHttps = (url: string) => {
    const httpReg = /http:\/\//gi;
    const domainReg =
        /umu\.(cn|co|com|tw)|\.umustatic\.(cn|co|com|tw)|\.bcebos\.com|\.amazonaws\.com|\.cloudfront\.net/gi;
    if (!url) {
        return 'javascript:;';
    }
    if (httpReg.test(url) && domainReg.test(url)) {
        url = url.replace('http://', window.location.protocol + '//');
    }
    return url;
};

const system: string = getCurrentSystem(); // CN/EN/JP/TW/EM
const orientalAreaArr: string[] = ['CN', 'TW', 'JP'];
/**
 * @desc 是不是东方国家
 */
export const isOrientalArea = orientalAreaArr.indexOf(system) !== -1;
